import React, { useState } from "react";
import { MapPin, Phone, Clock, Mail, Send } from "lucide-react";
import { firm, officeImage } from "../mock/mock";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";
import useReveal from "../hooks/useReveal";

const Contact = () => {
  const ref = useReveal();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please complete the required fields", variant: "destructive" });
      return;
    }
    // MOCK: store locally and confirm
    const saved = JSON.parse(localStorage.getItem("kdg_inquiries") || "[]");
    saved.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem("kdg_inquiries", JSON.stringify(saved));
    toast({ title: "Message sent", description: "Thank you — the office will reach out shortly." });
    setForm({ name: "", email: "", phone: "", message: "" });
  };

  const info = [
    { icon: MapPin, label: "Office", value: `${firm.address}, ${firm.city}` },
    { icon: Phone, label: "Phone", value: firm.phone, href: firm.phoneHref },
    { icon: Clock, label: "Hours", value: firm.hours },
    { icon: Mail, label: "Email", value: firm.email, href: `mailto:${firm.email}` },
  ];

  return (
    <section id="contact" className="py-24 bg-[#14213d] relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-10 bg-cover bg-center"
        style={{ backgroundImage: `url(${officeImage})` }}
      />
      <div className="relative max-w-7xl mx-auto px-6" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-14">
          <div className="reveal">
            <span className="inline-block text-[#c8a15a] uppercase tracking-[0.3em] text-xs font-semibold mb-4">
              Get In Touch
            </span>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white mb-5">
              Schedule a Consultation
            </h2>
            <p className="text-slate-300 text-lg mb-10 max-w-lg">
              Have a legal matter you’d like to discuss? Reach out and the office
              will get back to you promptly.
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              {info.map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <span className="flex items-center justify-center w-11 h-11 shrink-0 rounded-lg bg-[#c8a15a] text-[#14213d]">
                    <item.icon size={20} />
                  </span>
                  <div>
                    <div className="text-[#c8a15a] text-sm font-semibold uppercase tracking-wide">
                      {item.label}
                    </div>
                    {item.href ? (
                      <a href={item.href} className="text-slate-200 hover:text-white">
                        {item.value}
                      </a>
                    ) : (
                      <div className="text-slate-200">{item.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="reveal bg-white rounded-2xl p-8 shadow-2xl"
            style={{ animationDelay: "120ms" }}
          >
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name *</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Doe"
                  className="h-11"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email *</label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="jane@email.com"
                    className="h-11"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone</label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="(765) 000-0000"
                    className="h-11"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">How can I help? *</label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Briefly describe your legal matter..."
                  rows={5}
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#c8a15a] hover:bg-[#b58f45] text-[#14213d] font-semibold py-6 gap-2"
              >
                <Send size={18} /> Send Message
              </Button>
              <p className="text-xs text-slate-400 text-center">
                Submitting this form does not create an attorney-client relationship.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
